import React, { useContext, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyContext } from '../../../context/CompanyContext';
import { PdfTemplateContext } from '../../../context/PdfTemplateContext';
import { useToast } from '../../../providers/ToastProvider';
import { useRequest } from '../../../requests/common/requestHook';
import { PdfTemplateRequest } from '../../../requests/pdf.request';
import {
  PdfTemplate,
  PdfTemplateEntitySubtype,
  PdfTemplateEntityType,
  PdfTemplateInput,
} from '../../../types/pdf_template';
import { TranslationKey } from '../../../types/translation/TranslationKey';
import { translate } from '../../../types/translation/Translator';
import { Button, ButtonVariant, CancelButton } from '../../components/Button';
import { DropdownSelect } from '../../components/Dropdown';
import { Feedback } from '../../components/Feedback/feedback';
import { ActionModal } from '../../components/Modal';
import { TextInput } from '../../components/TextInput';
import { ToastType } from '../../components/Toast';
import PdfTemplateEditor from './PdfTemplateEditor';

export default function CreatePdfTemplateScreen() {
  const entityType = useParams()['entityType'] as PdfTemplateEntityType;
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { pdfTemplates, setPdfTemplates, pdfTemplatesLoading } = useContext(PdfTemplateContext);

  const lastTemplate = localStorage.getItem('lastPdfTemplate');

  const [template, setTemplate] = useState<PdfTemplateInput | null>(null);

  const [create, { loading }] = useRequest(PdfTemplateRequest.create);

  const handleCreate = async (template: PdfTemplateInput) => {
    const result = await create({ data: [template.forCreate()] });
    if (!result) return;

    result.forEach(created => pdfTemplates.set(created.id, new PdfTemplate(created)));
    setPdfTemplates(new Map(pdfTemplates));
    showToast({
      type: ToastType.success,
      title: translate({ key: TranslationKey.pdfTemplateCreatedSuccessfullyToastTitle }),
    });

    navigate(-1);
  };

  if (pdfTemplatesLoading) return <></>;

  return (
    <>
      <InitialPdfInputModal entityType={entityType} open={!template} onDone={template => setTemplate(template)} />

      {template && <PdfTemplateEditor template={template} onDone={template => handleCreate(template)} />}
    </>
  );
}

function InitialPdfInputModal({
  entityType,
  open,
  onDone,
}: {
  entityType: PdfTemplateEntityType;
  open: boolean;
  onDone: (template: PdfTemplateInput) => void;
}) {
  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);

  const { pdfTemplates } = useContext(PdfTemplateContext);
  const templatesWithEntityType = [...pdfTemplates.values()].filter(t => t.entityType === entityType);

  const [name, setName] = useState('');
  const [entitySubtype, setEntitySubtype] = useState<PdfTemplateEntitySubtype>(PdfTemplateEntitySubtype.inbound);

  const footer = (
    <div className='flex justify-end gap-2'>
      <CancelButton onClick={() => navigate(-1)} />
      <Button
        disabled={!name || !entitySubtype || templatesWithEntityType.some(t => t.name === name)}
        variant={ButtonVariant.primary}
        text={'Start'}
        onClick={() => onDone(new PdfTemplateInput({ companyId: currentCompany.id, entityType, name, entitySubtype }))}
      />
    </div>
  );

  return (
    <ActionModal title={'Create pdf template'} open={open} setOpen={() => {}} footer={footer}>
      <div className='grid gap-2'>
        <TextInput
          label={'Template name'}
          value={name}
          onChange={setName}
          feedback={value =>
            templatesWithEntityType.some(t => t.name === value)
              ? {
                  type: Feedback.Type.error,
                  text: 'Template name must be unique',
                }
              : undefined
          }
        />
        <DropdownSelect
          items={Object.values(PdfTemplateEntitySubtype)}
          textBuilder={s => s}
          value={entitySubtype}
          onSelect={setEntitySubtype}
          label={'Subtype'}
        />
      </div>
    </ActionModal>
  );
}
